/**
 * Reading links and invites as the interface holds them (parts 38d, 38b; decisions 58, 75): whether this build and
 * account can share (accounts on, a reader page named for links, decision 63's purchase), the records this device holds
 * and the account's lists as the relay gives them by session, pulled as the Sharing page opens and as a book opens, so
 * comments arrive as notes and the relay's word on invites reaches the records (DI8); and the invites' copy, pushed a
 * pause after a save, on hide and as the device comes online (DI6). Every relay call goes through {@link ShareLinks} and
 * {@link Invites}; the store through the workspace's share and invite stores.
 */
import { createSignal } from 'solid-js';
import type { InviteRecord, NoteRecord, ShareLinkRecord, ShareRecord } from '@cockatrice/storage';
import type { Account } from '../account/account.ts';
import { inviteUrl } from './invite-secrets.ts';
import { Invites, toBase64Id } from './invites.ts';
import { bytesOfHex } from './secrets.ts';
import type { ChangeOutcome, InviteChoice, InviteOutcome, InviteRequest, InviteStore, PushProblem } from './invites.ts';
import type { InviteState, ListedShare, OwnerInvite, OwnerList } from './invite-wire.ts';
import { ShareLinks } from './links.ts';
import { CopySchedule } from './push.ts';
import type { PushTimers } from './push.ts';
import type { SealedCopy, SealJob } from './seal.ts';
import type { LinkRequest, MakeOutcome, PendingLink, ReplyOutcome, RevokeOutcome, ShareStore } from './links.ts';
import type { LinkList } from './wire.ts';

/** What the page holds of a read: not read yet, read, or failed (the page says so and offers again). */
export type Read<T> = { readonly kind: 'loading' } | { readonly kind: 'ok'; readonly value: T } | { readonly kind: 'failed' };

/** What the interface's reading links run on. */
export interface SharingOptions {
  readonly account: Account;
  readonly store: ShareStore;
  /** Random bytes of a length. */
  readonly random: (length: number) => Uint8Array<ArrayBuffer>;
  readonly now: () => number;
  /** Part 38b: the invites' store, the app's origin (where invite links point), the sealing off the page and the timers. */
  readonly invites: {
    readonly store: InviteStore;
    readonly appOrigin: string;
    readonly seal: (job: SealJob) => Promise<SealedCopy | null>;
    readonly timers: PushTimers;
  };
}

/** The reading links of this device and account. */
export class Sharing {
  readonly #options: SharingOptions;
  readonly #links: ShareLinks | null;
  readonly #records = createSignal<Read<readonly ShareLinkRecord[]>>({ kind: 'loading' });
  readonly #listed = createSignal<Read<LinkList>>({ kind: 'loading' });
  readonly #comments = createSignal<ReadonlyMap<string, number>>(new Map());
  readonly #urls = createSignal<ReadonlyMap<string, string>>(new Map());
  #loading: Promise<void> | null = null;
  readonly #invites: Invites | null;
  readonly #schedule: CopySchedule | null;
  readonly #shares = createSignal<Read<readonly ShareRecord[]>>({ kind: 'loading' });
  readonly #accountShares = createSignal<Read<readonly ListedShare[]>>({ kind: 'loading' });
  /** The relay's lists of each share as last read, by record ID (the invites' states and activity). */
  readonly #lists = createSignal<ReadonlyMap<string, OwnerList>>(new Map());
  /** What last stopped each book's copy short, by book ID (table PU). */
  readonly #problems = createSignal<ReadonlyMap<string, PushProblem>>(new Map());

  constructor(options: SharingOptions) {
    this.#options = options;
    const { account } = options;
    const reader = account.readerOrigin();
    const subtle = account.subtle();
    const session = { get: <T>(path: string, read: (value: unknown) => T | null) => account.get(path, read), post: <T>(path: string, body: unknown, read: (value: unknown) => T | null) => account.post(path, body, read), postBytes: <T>(path: string, bytes: Uint8Array<ArrayBuffer>, read: (value: unknown) => T | null) => account.postBytes(path, bytes, read) };
    this.#invites =
      subtle === null
        ? null
        : new Invites({
            session,
            relay: () => account.publicClient(),
            store: options.invites.store,
            subtle,
            random: options.random,
            now: options.now,
            appOrigin: options.invites.appOrigin,
            seal: options.invites.seal,
            device: () => account.deviceSpki(),
            // Fix-next 84, table BO: whose list says which shares are the account's own.
            account: () => account.accountId(),
          });
    this.#schedule = this.#invites === null ? null : new CopySchedule((bookId) => this.reconcile(bookId), options.invites.timers);
    this.#links =
      reader === null || subtle === null
        ? null
        : new ShareLinks({
            session,
            relay: () => account.publicClient(),
            store: options.store,
            subtle,
            random: options.random,
            now: options.now,
            readerOrigin: reader,
          });
  }

  /** Whether this build makes reading links at all (accounts on, a reader page named) and the device is logged in. */
  readonly available = (): boolean => {
    const status = this.#options.account.status();
    return this.#links !== null && status.kind === 'account' && !status.signedOut;
  };

  /**
   * Deep-fix r2, RT4: whether this build reaches reading links at all (accounts on, a reader page named): what pulling
   * and replying need, by the record alone (principle 9), signed in or not.
   */
  readonly configured = (): boolean => this.#links !== null;

  /** DP8, decision 63: whether the account's copy says it is paid (the relay decides; this only shows the form). */
  readonly paid = (): boolean => this.#options.account.standing().kind === 'paid';

  /** The records this device holds, as last read. */
  readonly records = (): Read<readonly ShareLinkRecord[]> => this.#records[0]();

  /** The account's links as the relay last listed them. */
  readonly listed = (): Read<LinkList> => this.#listed[0]();

  /**
   * Deep-fix r5, CU: the link a reader opens for `record`, worked out when the records were read, so Copy link writes it
   * in the press itself (a browser lets a page write the clipboard only there); undefined before.
   */
  readonly urlNow = (record: ShareLinkRecord): string | undefined => this.#urls[0]().get(record.id);

  /** The readers' notes standing, by the link's record they came through, as last read. */
  readonly comments = (): ReadonlyMap<string, number> => this.#comments[0]();


  /** LW1: makes a link of `request`, the records read again once it is made. */
  async make(request: LinkRequest): Promise<MakeOutcome> {
    const links = this.#links;
    if (links === null) return { kind: 'failed' };
    const made = await links.make(request);
    await this.#readRecords(links);
    return made;
  }

  /** LW1: sends a link's parts again. */
  async upload(pending: PendingLink): Promise<MakeOutcome> {
    const links = this.#links;
    if (links === null) return { kind: 'failed' };
    const made = await links.upload(pending);
    await this.#readRecords(links);
    return made;
  }

  /**
   * The Sharing page's read: the records, every one pulled (its comments in as notes, its end learned, table PL), then
   * the records and the account's list read again. A read already under way is the one given.
   */
  load(): Promise<void> {
    this.#loading ??= this.#load().finally(() => {
      this.#loading = null;
    });
    return this.#loading;
  }

  async #load(): Promise<void> {
    await this.#loadInvites();
    const links = this.#links;
    if (links === null) return;
    // Deep-fix r1, RT3: the records as stored first, so nothing waits on the pulls to know them; again once pulled.
    await this.#readRecords(links);
    for (const record of await this.#options.store.links()) if (record.ended?.why !== 'gone') await links.pull(record);
    await this.#readRecords(links);
    const listed = await links.list();
    this.#listed[1](listed === null ? { kind: 'failed' } : { kind: 'ok', value: listed });
  }

  /** PL, DI6, DI8: as book `bookId` opens: its links' comments pulled in, its invites read against the relay and its copy pushed. */
  async pullBook(bookId: string): Promise<void> {
    const invites = this.#invites;
    if (invites !== null) {
      await this.#readShares(invites);
      if (this.invitesAvailable()) this.#schedule?.now(bookId);
    }
    const links = this.#links;
    if (links === null) return;
    await this.#readRecords(links);
    for (const record of await this.#options.store.links()) if (record.bookId === bookId && record.ended?.why !== 'gone') await links.pull(record);
    await this.#readRecords(links);
  }

  /** Part 38b: whether this build invites at all (accounts on) and the device is logged in. */
  readonly invitesAvailable = (): boolean => {
    const status = this.#options.account.status();
    return this.#invites !== null && status.kind === 'account' && !status.signedOut;
  };

  /** Part 38b: the share records this device holds, as last read. */
  readonly shares = (): Read<readonly ShareRecord[]> => this.#shares[0]();

  /**
   * Part 38e (DN1): the share a new note of book `bookId` can go across: its newest open share record, as last read, while
   * this build invites and the device is logged in; undefined otherwise (the composer offers Only me alone).
   */
  readonly openShareOf = (bookId: string | null): ShareRecord | undefined => {
    const read = this.shares();
    if (read.kind !== 'ok' || !this.invitesAvailable()) return undefined;
    // Fix-next 84, BO3: a share the account's list (as last read) doesn't name is another account's: no note goes across
    // it (its pushes never run either, `Invites`). Unread, none is ruled out here; the push still waits to know.
    const listed = this.accountShares();
    const named = listed.kind === 'ok' ? new Set(listed.value.map((share) => share.id)) : null;
    return read.value
      .filter((record) => record.bookId === bookId && record.ended === null && record.made && (named === null || named.has(toBase64Id(record.id))))
      .sort((a, b) => b.createdAt - a.createdAt)[0];
  };

  /** Part 38e: invite `id` of share record `share` (a note's author or thread), if this device holds it (PN13: else none). */
  readonly inviteOf = (share: string | undefined, id: string | null | undefined): InviteRecord | undefined => {
    const read = this.shares();
    if (read.kind !== 'ok') return undefined;
    return read.value.find((record) => record.id === share)?.invites.find((invite) => invite.id === id);
  };

  /** IW4: the account's shares as the relay last listed them by session. */
  readonly accountShares = (): Read<readonly ListedShare[]> => this.#accountShares[0]();

  /** DI8: the relay's last list of share record `id`, if read. */
  readonly listOf = (id: string): OwnerList | undefined => this.#lists[0]().get(id);

  /** Table PU: what last stopped book `bookId`'s copy short, if anything. */
  readonly problemOf = (bookId: string): PushProblem | undefined => this.#problems[0]().get(bookId);

  /** The relay's last state of `invite` of `record`, if listed. */
  stateOf(record: ShareRecord, invite: InviteRecord): InviteState | undefined {
    return this.listedOf(record, invite)?.state;
  }

  /** The relay's last word on `invite` of `record` (its state, when accepted, its notes), if listed; reactive. */
  listedOf(record: ShareRecord, invite: InviteRecord): OwnerInvite | undefined {
    const id = toBase64Id(invite.id);
    return this.listOf(record.id)?.invites.find((listed) => listed.id === id);
  }

  /** IW1: the invite link of `invite` (Resend: the same link; its secrets and the app's origin are all it takes). */
  linkOf(invite: InviteRecord): string {
    return inviteUrl(this.#options.invites.appOrigin, { claim: bytesOfHex(invite.claim), key: bytesOfHex(invite.key) });
  }

  /** DI8: book `bookId`'s shares read against the relay (as its people dialog opens: who accepted, who reset), then read again. */
  async refreshBook(bookId: string): Promise<void> {
    const invites = this.#invites;
    if (invites === null || !this.invitesAvailable()) return;
    await invites.refresh(bookId);
    await this.#readShares(invites);
  }

  /** IW1: invites a person, the records read again after. */
  async invite(request: InviteRequest): Promise<InviteOutcome> {
    const invites = this.#invites;
    if (invites === null) return { kind: 'failed' };
    const invited = await invites.invite(request);
    await this.#readShares(invites);
    if (invited.kind === 'invited') this.#schedule?.now(request.bookId);
    // Fix-next 67, UR2: kept unsent (the network, a server error): tried again soon, not only at the next save or online.
    this.#schedule?.retry(request.bookId, invited.kind === 'unsent');
    return invited;
  }

  /** IW11: a person's settings changed, the records read again after, the copy pushed. */
  async change(bookId: string, id: string, choice: InviteChoice): Promise<ChangeOutcome> {
    const invites = this.#invites;
    if (invites === null) return { kind: 'failed' };
    const changed = await invites.change(bookId, id, choice);
    await this.#readShares(invites);
    if (changed.kind === 'done') this.#schedule?.now(bookId);
    return changed;
  }

  /** IW5: a person's access removed, the records read again after. */
  async remove(bookId: string, id: string): Promise<ChangeOutcome> {
    const invites = this.#invites;
    if (invites === null) return { kind: 'failed' };
    const removed = await invites.remove(bookId, id);
    await this.#readShares(invites);
    return removed;
  }

  /** IW2: a new invite for a person after their reset, the records read again after. */
  async renew(bookId: string, id: string): Promise<InviteOutcome> {
    const invites = this.#invites;
    if (invites === null) return { kind: 'failed' };
    const renewed = await invites.renew(bookId, id);
    await this.#readShares(invites);
    if (renewed.kind === 'invited') this.#schedule?.now(bookId);
    this.#schedule?.retry(bookId, renewed.kind === 'unsent');
    return renewed;
  }

  /** IW6, IW8: every shared book's access ended by session; how many shares ended, or null. */
  async endAllShares(): Promise<number | null> {
    const invites = this.#invites;
    if (invites === null) return null;
    const ended = await invites.endAll();
    await this.#readShares(invites);
    await this.#readAccountShares();
    return ended;
  }

  /** Table PU: book `bookId`'s copy brought to the relay; what stopped it short kept for the Sharing page. */
  async reconcile(bookId: string): Promise<void> {
    const invites = this.#invites;
    if (invites === null || !this.invitesAvailable()) {
      this.#schedule?.retry(bookId, false);
      return;
    }
    const done = await invites.reconcile(bookId);
    // Fix-next 67, UR2: each run's answer sets the next try of an unsent invite, or ends the tries.
    this.#schedule?.retry(bookId, done.unsent === true);
    const problems = new Map(this.#problems[0]());
    if (done.problem === null) problems.delete(bookId);
    else problems.set(bookId, done.problem);
    this.#problems[1](problems);
    await this.#readShares(invites);
  }

  /** DI6: a save of book `bookId`'s open chapter: its copy waits a pause. */
  saved(bookId: string): void {
    if (this.invitesAvailable()) this.#schedule?.saved(bookId);
  }

  /** DI6: the page hidden: a copy waiting goes now. */
  hidden(): void {
    this.#schedule?.flush();
  }

  /**
   * Deep-fix r6 (RC's starts): the page shown again with book `bookId` open: a copy a problem stopped is tried again now.
   * Fix-next 67, UR8: not a later version held (`stale`): trying it again sends the chapters whole and is refused again
   * until the clock passes it; the next save tries it.
   */
  visible(bookId: string | undefined): void {
    const problem = bookId === undefined ? undefined : this.problemOf(bookId);
    if (bookId !== undefined && problem !== undefined && problem !== 'stale' && this.invitesAvailable()) this.#schedule?.now(bookId);
  }

  /** DI6: the device back online with book `bookId` open (none: undefined): its copy goes now. */
  online(bookId: string | undefined): void {
    if (bookId !== undefined && this.invitesAvailable()) this.#schedule?.now(bookId);
  }

  /** The share records read again, with the relay's lists the invites hold. */
  async #readShares(invites: Invites): Promise<void> {
    const records = await this.#options.invites.store.shares();
    this.#shares[1]({ kind: 'ok', value: records });
    this.#lists[1](new Map(records.flatMap((record) => {
      const list = invites.listOf(record.id);
      return list === undefined ? [] : [[record.id, list] as const];
    })));
  }

  /** IW4: the account's shares by session, read again. */
  async #readAccountShares(): Promise<void> {
    const listed = (await this.#invites?.accountShares()) ?? null;
    this.#accountShares[1](listed === null ? { kind: 'failed' } : { kind: 'ok', value: listed });
  }

  /** The Sharing page's read of the invites: the records, each read against the relay (DI8), then the account's list. */
  async #loadInvites(): Promise<void> {
    const invites = this.#invites;
    if (invites === null) return;
    await this.#readShares(invites);
    if (!this.invitesAvailable()) return;
    await invites.refresh();
    await this.#readShares(invites);
    await this.#readAccountShares();
  }

  /** The record of the link a reader's note came through, as last read; undefined when none is held here. */
  recordOf(note: NoteRecord): ShareLinkRecord | undefined {
    const records = this.records();
    const id = note.link?.link;
    // Deep-fix r1, RT1: this book's own link only (a copy of a book keeps its notes' links, never the link's record).
    return records.kind === 'ok' && id !== undefined ? records.value.find((record) => record.id === id && record.bookId === note.bookId) : undefined;
  }

  /** SL11: the writer's reply to a reader's thread; `local` when it can only be the writer's own. */
  async reply(note: NoteRecord, text: string): Promise<ReplyOutcome> {
    const links = this.#links;
    if (links === null) return { kind: 'local' };
    const replied = await links.reply(note, text);
    // Deep-fix r5, RE: a reply that didn't go (the link ended meanwhile) reads the records again, so the thread says so.
    if (replied.kind !== 'sent') await this.#readRecords(links);
    return replied;
  }

  /** Table RV: revokes `record` by its record, then reads again. */
  async revoke(record: ShareLinkRecord): Promise<RevokeOutcome> {
    if (this.#links === null) return { kind: 'failed' };
    const revoked = await this.#links.revoke(record);
    await this.#readAll(this.#links);
    return revoked;
  }

  /** LW3: ends every link of the account by session, then reads again; how many ended, or null. */
  async revokeAll(): Promise<number | null> {
    if (this.#links === null) return null;
    const ended = await this.#links.revokeAll();
    await this.#readAll(this.#links);
    return ended;
  }

  async #readRecords(links: ShareLinks): Promise<void> {
    const records = await this.#options.store.links();
    const comments = new Map<string, number>();
    for (const bookId of new Set(records.map((record) => record.bookId))) for (const [link, count] of await this.#options.store.readerNotes(bookId)) comments.set(link, count);
    const urls = new Map<string, string>();
    for (const record of records) {
      urls.set(record.id, await links.urlOf(record));
    }
    this.#records[1]({ kind: 'ok', value: records });
    this.#comments[1](comments);
    this.#urls[1](urls);
  }

  async #readAll(links: ShareLinks): Promise<void> {
    await this.#readRecords(links);
    const listed = await links.list();
    this.#listed[1](listed === null ? { kind: 'failed' } : { kind: 'ok', value: listed });
  }
}
